import React from 'react';

import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { Product } from '@graphql/generated';

const PLUS_PLAN_NAME = 'plus';

interface PlanItemWebProps {
  product: Product;
  isMonthly: boolean;
  isSelected: boolean;
  disabled?: boolean;
  onPress: () => void;
  percentageSaved?: number;
  showBillingLabel?: boolean;
  testID?: string;
}

/**
 * Shared plan selection card used in CreateSubscription and EditSubscription.
 * Displays plan name, icon, pricing with optional annual discount badge.
 */
export const PlanItemWeb = ({
  product,
  isMonthly,
  isSelected,
  disabled = false,
  onPress,
  percentageSaved,
  showBillingLabel = true,
  testID,
}: PlanItemWebProps) => {
  const monthPrice = product.prices.filter((t) => t.interval === 'month');
  const yearlyPrice = product.prices.filter((t) => t.interval === 'year');
  const discountedPrice =
    yearlyPrice.length > 0 ? yearlyPrice[0].amount / 12 : 0;
  const price = monthPrice.length > 0 ? monthPrice[0].amount : 0;

  return (
    <Box
      testID={testID}
      flex={1}
      borderColor='grey02'
      borderWidth={1}
      borderRadius='xs'
      flexDirection='row'
      accessibilityLabel={product.name}
      p='m'
      marginTop='xs'
      alignItems='center'>
      <RadioButton
        testID={testID ? `radio-button-${product.id}` : undefined}
        isSelected={isSelected}
        disabled={disabled}
        onPress={onPress}
      />
      <Icon
        marginHorizontal='s'
        name={
          product?.name?.toLowerCase()?.includes(PLUS_PLAN_NAME)
            ? 'Zap'
            : 'Star'
        }
        backgroundColor={
          product?.name?.toLowerCase()?.includes(PLUS_PLAN_NAME)
            ? 'orange'
            : 'purple'
        }
        color='background'
      />
      <Box flex={1}>
        <Box flexDirection='row' alignItems='center'>
          <Text variant='labelEmphasized' mb='xxxs' color='textPrimary'>
            {product.name + ' Plan'}
          </Text>

          {!isMonthly && percentageSaved != null && (
            <Box
              marginHorizontal='xs'
              style={{
                backgroundColor: '#DCF2EC',
                borderRadius: 4,
                padding: 4,
              }}>
              <Text variant='metadata' color='greenSecondary'>
                {percentageSaved}% off
              </Text>
            </Box>
          )}
        </Box>
        <Text variant='webBodySecondary' color='grey04'>
          {showBillingLabel && 'Billed ' + (isMonthly ? 'Monthly' : 'Annually')}
        </Text>
      </Box>
      <Box
        alignContent='flex-end'
        justifyContent='flex-end'
        alignItems='flex-end'>
        <Box flexDirection='row' marginRight='xxxs'>
          <Text
            variant={
              isMonthly ? 'labelEmphasized' : 'labelDisabledStrikeThrough'
            }
            mt='xxs'
            marginRight='xxs'>
            ${(price / 100)?.toFixed(0)}
          </Text>
          {!isMonthly && (
            <Text variant='labelEmphasized' mb='xxxs' color='textPrimary'>
              ${(discountedPrice / 100).toFixed(0)}
            </Text>
          )}
        </Box>
        <Text variant='webBodySecondary' color='grey04'>
          per user per month
        </Text>
      </Box>
    </Box>
  );
};
